import * as React from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@rtkwlf/ui';
import Icon from '../../../widgets/Icons';
import { sessionSelector } from '../../../reducers/session';
import { onboardingSelector } from '../../../reducers/onboarding';
import { memoizedOnboardingCompletionSelectors } from '../../../selectors/onboardingCompletionSelectors';
import { getPluralOrSingularNoun } from '../../../utils';
import { useSubmitModal } from './OnboardingSubmitModal';
import { OnboardingProgress } from './OnboardingProgress';
import { OnboardingCompletedView } from './OnboardingCompletedView';

export const OnboardingChecklist: React.FunctionComponent = () => {
  const contactsVisible = useSelector(
    onboardingSelector.getSectionVisibility.contacts,
  );

  const sitesVisible = useSelector(
    onboardingSelector.getSectionVisibility.sites,
  );

  const endpointConfigVisible = useSelector(
    onboardingSelector.getSectionVisibility.endpoint,
  );

  const cloudSensorsVisible = useSelector(
    onboardingSelector.getSectionVisibility.cloudSensors,
  );

  const internetExposureVisible = useSelector(
    onboardingSelector.getSectionVisibility.internetExposure,
  );

  const contactsCompleted = useSelector(
    memoizedOnboardingCompletionSelectors.getContactsCompleted,
  );

  const sitesConfigCompleted = useSelector(
    onboardingSelector.getSiteConfigCompleted,
  );
  const internetExposureCompleted = useSelector(
    onboardingSelector.getInternetExposureCompleted,
  );
  const cloudConfigCompleted = useSelector(
    onboardingSelector.getCloudSensorConfigCompleted,
  );
  const endpointConfigCompleted = useSelector(
    onboardingSelector.getEndpointConfigCompleted,
  );

  // can only edit if data entry isn't completed and submitted
  const canEdit = useSelector(sessionSelector.canEditOnboarding);

  const { allCompleted, incompleteSections, totalSections } = useSelector(
    memoizedOnboardingCompletionSelectors.getCompletionStatus,
  );

  const TKOCompleted = useSelector(sessionSelector.hasCompletedTKO);

  const { modal: submitModal, openModal: onSubmit } = useSubmitModal();

  const { banner, description } = React.useMemo(() => {
    // form incomplete
    if (!allCompleted) {
      return {
        banner: '',
        description: `${getPluralOrSingularNoun(
          incompleteSections,
          'incomplete section',
        )} remaining.`,
      };
    }

    let banner, description;

    if (canEdit) {
      // form complete, review not requested yet
      banner =
        'You are now ready to submit your information to your Onboarding team';
      description = TKOCompleted
        ? 'Please submit your information to begin the review process.'
        : 'Please schedule your Technical Kickoff Call to review your information.';
    } else {
      // form complete, under review
      banner = TKOCompleted
        ? 'Your information is being reviewed by your Onboarding team'
        : "You're all set for your Technical Kickoff Call with your Onboarding Team";
      description =
        'All sections are currently read-only in order to facilitate the review process.';
    }

    return {
      banner,
      description,
    };
  }, [allCompleted, incompleteSections, canEdit, TKOCompleted]);

  if (totalSections === 0) {
    return <OnboardingCompletedView />;
  }

  return (
    <div>
      {allCompleted && (
        <div
          style={{
            position: 'fixed',
            top: '64px',
            backgroundColor: '#58595B',
            right: 0,
            left: 0,
            zIndex: 1,
          }}
          data-cy="submitOBFormBanner"
        >
          <div className="d-flex mt-2 mb-2 justify-content-center align-items-center">
            <label
              style={{ color: 'white', fontSize: '14px', marginRight: '20px' }}
            >
              {banner}
            </label>
            {canEdit && (
              <Button
                type="primary"
                ghost
                onClick={onSubmit}
                data-cy="submitOBFormButton"
              >
                Submit Information
              </Button>
            )}
          </div>
        </div>
      )}

      <h2 className="ml-3">Checklist</h2>
      <div className="ob-checklist-grid-container pl-3">
        <OnboardingProgress />

        <label style={{ gridArea: 'text' }} data-cy="OBProgressDescription">
          {description}
        </label>

        <div
          style={{ gridArea: 'section' }}
          className="d-flex flex-sm-wrap ml-5 align-items-center"
        >
          {contactsVisible && (
            <div className="ob-section-checklist" data-cy="contactsChecklist">
              {contactsCompleted ? (
                <Icon.CheckCircle className="ob-checklist-checkmark" />
              ) : (
                <Icon.CircleSolid className="ob-checklist-circle" />
              )}
              <label>Contacts</label>
            </div>
          )}

          {sitesVisible && (
            <div className="ob-section-checklist" data-cy="siteChecklist">
              {sitesConfigCompleted ? (
                <Icon.CheckCircle className="ob-checklist-checkmark" />
              ) : (
                <Icon.CircleSolid className="ob-checklist-circle" />
              )}
              <label>Site Configuration</label>
            </div>
          )}

          {endpointConfigVisible && !TKOCompleted && (
            <div
              className="ob-section-checklist"
              data-cy="endpointConfigChecklist"
            >
              {endpointConfigCompleted ? (
                <Icon.CheckCircle className="ob-checklist-checkmark" />
              ) : (
                <Icon.CircleSolid className="ob-checklist-circle" />
              )}
              <label>Endpoint Configuration</label>
            </div>
          )}

          {cloudSensorsVisible && (
            <div className="ob-section-checklist" data-cy="cloudChecklist">
              {cloudConfigCompleted ? (
                <Icon.CheckCircle className="ob-checklist-checkmark" />
              ) : (
                <Icon.CircleSolid className="ob-checklist-circle" />
              )}
              <label>Cloud Configuration</label>
            </div>
          )}

          {internetExposureVisible && (
            <div
              className="ob-section-checklist"
              data-cy="internetExposureChecklist"
            >
              {internetExposureCompleted ? (
                <Icon.CheckCircle className="ob-checklist-checkmark" />
              ) : (
                <Icon.CircleSolid className="ob-checklist-circle" />
              )}
              <label>Internet Exposure</label>
            </div>
          )}
        </div>
      </div>
      {submitModal}
    </div>
  );
};

export default OnboardingChecklist;
